<template>
  <VanPopup
    :show="show"
    @update:show="$emit('update:show', $event)"
    class="w-full"
    :close-on-click-overlay="false"
    teleport="body"
    closeable
  >
    <VanTabs v-model:active="activeTab" class="pt-10">
      <VanTab :title="$t('Chats.chatScene.edit.winTarget')">
        <VanField
          :model-value="
            winTarget.scoreId &&
            numericalOptions.find((item) => item.value === winTarget.scoreId)
              ?.text
          "
          @update:model-value="winTarget.scoreId = $event"
          is-link
          readonly
          required
          :label="$t('Chats.chatScene.edit.numerical')"
          :placeholder="$t('Chats.chatScene.edit.numericalSelect')"
          @click="openNumericalPicker"
        />

        <VanField
          :model-value="
            (winTarget.targetCharacterId &&
              characterOptions.find(
                (item) => item.value === winTarget.targetCharacterId
              )?.text) ||
            $t(`game.player`)
          "
          v-if="winTarget.scoreId && characterOptions.length > 0"
          @update:model-value="winTarget.targetCharacterId = $event"
          is-link
          required
          readonly
          :label="$t('Chats.chatScene.edit.character')"
          :placeholder="$t('Chats.chatScene.edit.characterSelect')"
          @click="openCharacterPicker"
        />
        <VanField
          is-link
          readonly
          :model-value="
            (winTarget.targetOperator || '>=') + winTarget.targetScore
          "
          :label="$t('Chats.chatScene.edit.condition')"
          required
          @click="clickOpenScoreSliderModal"
        />
        <div class="px-4 py-2 bg-base-200">
          <div class="flex pl-2.5 mb-1">
            {{ $t("Chats.chatScene.edit.endingScript") }}
          </div>
          <GenerableTextArea
            v-model="winTarget.endingScript"
            :enable-generate="false"
            :label="$t('User.mate.chat.systemPrompt')"
            :promptType="MateCardPromptType.Greeting"
          />
        </div>
      </VanTab>
      <VanTab :title="$t('Chats.chatScene.edit.failReason')">
        <VanField
          :model-value="
            failTarget.scoreId &&
            numericalOptions.find((item) => item.value === failTarget.scoreId)
              ?.text
          "
          @update:model-value="failTarget.scoreId = $event"
          is-link
          readonly
          required
          :label="$t('Chats.chatScene.edit.numerical')"
          :placeholder="$t('Chats.chatScene.edit.numericalSelect')"
          @click="openNumericalPicker"
        />
        <VanField
          v-if="failTarget.scoreId && characterOptions.length > 0"
          :model-value="
            failTarget.targetCharacterId &&
            characterOptions.find(
              (item) => item.value === failTarget.targetCharacterId
            )?.text
          "
          @update:model-value="failTarget.targetCharacterId = $event"
          is-link
          readonly
          required
          :label="$t('Chats.chatScene.edit.character')"
          :placeholder="$t('Chats.chatScene.edit.characterSelect')"
          @click="openCharacterPicker"
        />
        <VanField
          is-link
          readonly
          :model-value="
            (failTarget.targetOperator || '>=') + failTarget.targetScore
          "
          :label="$t('Chats.chatScene.edit.condition')"
          required
          @click="clickOpenScoreSliderModal"
        />
        <div class="px-4 py-2 bg-base-200">
          <div class="flex pl-2.5 mb-1">
            {{ $t("Chats.chatScene.edit.endingScript") }}
          </div>
          <GenerableTextArea
            v-model="failTarget.endingScript"
            :enable-generate="false"
            :label="$t('User.mate.chat.systemPrompt')"
            :promptType="MateCardPromptType.Greeting"
          />
        </div>
      </VanTab>
    </VanTabs>

    <div class="flex justify-center gap-4 py-4 bg-base-200">
      <!-- <button
        class="btn btn-sm btn-neutral text-base-content/50 gap-0"
        @click="emit('update:show', false)"
      >
        {{ $t("Buttons.cancel") }}
      </button> -->

      <button
        class="btn btn-sm text-base-content/50 gap-0"
        v-if="showDelete"
        @click="clickDelete"
      >
        {{ $t("Buttons.delete") }}
      </button>

      <button class="btn btn-sm btn-primary" @click="clickSave">
        {{ $t("Buttons.save") }}
      </button>
    </div>
  </VanPopup>
  <BaseScoreSliderModal
    v-model:show="showScoreSliderModal"
    :title="showScoreTitle"
    :score="showScore"
    @confirm="confirmScore"
    :operator="showScoreOperator"
    with-operator
  />

  <ModalSelectOption
    v-model:show="showNumericalPicker"
    :options="numericalOptions"
    @confirm="confirmNumerical"
  />

  <ModalSelectOption
    v-model:show="showCharacterPicker"
    :options="characterOptions"
    @confirm="confirmCharacter"
  />
</template>

<script setup lang="ts">
import {
  createChatSceneCharacterTarget,
  deleteChatSceneCharacterTarget,
  updateChatSceneCharacterTarget,
} from "@/apis/chatScene";
import { ChatSceneTargetType, MateCardPromptType } from "@/enums/chatEnums";
import { useChatScene } from "@/stores/chatScene";
import { useMate } from "@/stores/mate";
import {
  ChatScene,
  ChatSceneCharacter,
  ChatSceneCharacterTarget,
} from "@/types/chat";
import { showConfirmDialog } from "vant";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const activeTab = ref(0);
const props = defineProps<{
  show: boolean;
  character: ChatSceneCharacter;
  chatScene: ChatScene;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

const chatSceneStore = useChatScene();

const getDefaultTarget = (type: ChatSceneTargetType) => ({
  characterId: props.character?.id! || 0,
  targetScore: 0,
  targetOperator: ">=",
  type,
  endingScript: "",
});
const winTarget = ref<ChatSceneCharacterTarget>(
  getDefaultTarget(ChatSceneTargetType.WINNING)
);
const failTarget = ref<ChatSceneCharacterTarget>(
  getDefaultTarget(ChatSceneTargetType.LOSING)
);

watch(
  () => activeTab.value,
  (newVal) => {
    if (newVal === 0) {
      initCharacterOptions(winTarget.value.scoreId);
    } else if (newVal === 1) {
      initCharacterOptions(failTarget.value.scoreId);
    }
  }
);
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      const win = props.character.targetzz?.find(
        (target) => target.type === ChatSceneTargetType.WINNING
      );
      winTarget.value = win || getDefaultTarget(ChatSceneTargetType.WINNING);
      initCharacterOptions(winTarget.value.scoreId);
      const lose = props.character.targetzz?.find(
        (target) => target.type === ChatSceneTargetType.LOSING
      );
      failTarget.value = lose || getDefaultTarget(ChatSceneTargetType.LOSING);

      numericalOptions.value =
        props.chatScene.scorezz?.map((score) => ({
          text: score.name,
          value: score.id!,
        })) || [];
    }
  }
);

// 人物选择
const showCharacterPicker = ref(false);
const mateStore = useMate();
const characterOptions = ref<{ text: string; value: number }[]>([]);
const openCharacterPicker = () => {
  showCharacterPicker.value = true;
};

const confirmCharacter = (characterId: number) => {
  showCharacterPicker.value = false;
  if (activeTab.value === 0) {
    winTarget.value.targetCharacterId = characterId;
  } else if (activeTab.value === 1) {
    failTarget.value.targetCharacterId = characterId;
  }
};

const initCharacterOptions = (scoreId?: number) => {
  if (!scoreId) {
    characterOptions.value = [];
    return;
  }
  const activeScore = props.chatScene.scorezz?.find(
    (score) => score.id === scoreId
  );
  characterOptions.value =
    activeScore?.characterzz?.map((character) => ({
      text: character.mateId
        ? mateStore.mateIdMap.get(character.mateId)?.name!
        : t(`game.player`),
      value: character.id!,
    })) || [];
  if (
    activeTab.value === 0 &&
    characterOptions.value.length > 0 &&
    (!winTarget.value.targetCharacterId ||
      !characterOptions.value.some(
        (item) => item.value === winTarget.value.targetCharacterId
      ))
  ) {
    winTarget.value.targetCharacterId = characterOptions.value[0]?.value || 0;
  } else if (
    activeTab.value === 1 &&
    characterOptions.value.length > 0 &&
    (!failTarget.value.targetCharacterId ||
      !characterOptions.value.some(
        (item) => item.value === failTarget.value.targetCharacterId
      ))
  ) {
    failTarget.value.targetCharacterId = characterOptions.value[0]?.value || 0;
  }
};

// 数值选择
const showNumericalPicker = ref(false);
const numericalOptions = ref<{ text: string; value: number }[]>([]);
const openNumericalPicker = () => {
  showNumericalPicker.value = true;
};

const confirmNumerical = (scoreId: number) => {
  showNumericalPicker.value = false;
  if (activeTab.value === 0) {
    winTarget.value.scoreId = scoreId;
  } else if (activeTab.value === 1) {
    failTarget.value.scoreId = scoreId;
  }
  initCharacterOptions(scoreId);
};

// condition 条件选择
const showScoreSliderModal = ref(false);
const showScore = ref(0);
const showScoreTitle = ref("");
const showScoreOperator = ref(">=");
const clickOpenScoreSliderModal = () => {
  showScoreSliderModal.value = true;
  showScore.value =
    activeTab.value === 0
      ? winTarget.value.targetScore
      : failTarget.value.targetScore;
  showScoreTitle.value =
    activeTab.value === 0
      ? t("Chats.chatScene.edit.winTarget")
      : t("Chats.chatScene.edit.failReason");
  showScoreOperator.value =
    activeTab.value === 0
      ? winTarget.value.targetOperator
      : failTarget.value.targetOperator;
};

const confirmScore = (score: number, targetOperator = ">=") => {
  showScoreSliderModal.value = false;
  if (activeTab.value === 0) {
    winTarget.value.targetScore = score;
    winTarget.value.targetOperator = targetOperator;
  } else if (activeTab.value === 1) {
    failTarget.value.targetScore = score;
    failTarget.value.targetOperator = targetOperator;
  }
};

const clickSave = async () => {
  emit("update:show", false);
  chatSceneStore.withSaving(async () => {
    if (winTarget.value.scoreId) {
      if (winTarget.value.id) {
        const { item } = await updateChatSceneCharacterTarget(
          winTarget.value.id,
          winTarget.value
        );
        props.character.targetzz = props.character.targetzz!.map((target) =>
          target.id === item.id ? item : target
        );
      } else {
        const { item } = await createChatSceneCharacterTarget(winTarget.value);
        props.character.targetzz = [...props.character.targetzz!, item];
      }
    }
    if (failTarget.value.scoreId) {
      if (failTarget.value.id) {
        const { item } = await updateChatSceneCharacterTarget(
          failTarget.value.id,
          failTarget.value
        );
        props.character.targetzz = props.character.targetzz!.map((target) =>
          target.id === item.id ? item : target
        );
      } else {
        const { item } = await createChatSceneCharacterTarget(failTarget.value);
        props.character.targetzz = [...props.character.targetzz!, item];
      }
    }
  });
};

const showDelete = computed(() => {
  if (activeTab.value === 0 && winTarget.value.id) {
    return true;
  } else if (activeTab.value === 1 && failTarget.value.id) {
    return true;
  }
  return false;
});

const clickDelete = () => {
  showConfirmDialog({
    title: t("Message.deleteConfirm"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(() => {
    let id = 0;
    if (activeTab.value === 0) {
      props.character.targetzz = props.character.targetzz!.filter(
        (target) => target.id !== winTarget.value.id
      );
      id = winTarget.value.id!;
    } else if (activeTab.value === 1) {
      props.character.targetzz = props.character.targetzz!.filter(
        (target) => target.id !== failTarget.value.id
      );
      id = failTarget.value.id!;
    }
    chatSceneStore.withSaving(async () => {
      emit("update:show", false);
      await deleteChatSceneCharacterTarget(id);
    });
  });
};
</script>
